/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactNode } from 'react';

import { RowColumn, RowColumnProps } from '../../Containers';
import { RadioGroupProps } from './Group';
import { Radio, RadioProps } from './Radio';
import { RadioGroup } from '../Radio';

import { pick } from 'utils/object';
import { cn } from 'styles';

type Orientation = 'horizontal' | 'vertical';

type WrapperProperties = Pick<
  RowColumnProps,
  | 'alignItems'
  | 'gap'
  | 'flexWrap'
  | 'fullHeight'
  | 'fullWidth'
  | 'justifyContent'
  | 'inLine'
  | 'position'
  | 'relative'
>;

type GroupProps = Pick<
  RadioGroupProps,
  | 'color'
  | 'size'
  | 'disabled'
  | 'name'
  | 'id'
  | 'defaultValue'
  | 'value'
  | 'onChangeDirect'
  | 'onChange'
>;

type RadioBaseProps = Pick<RadioProps, 'icon' | 'iconSize' | 'addDot'>;

type RenderBaseParams = Parameters<Exclude<RadioProps['render'], undefined>>[0];

type RadioOption = {
  value: string;
  label: ReactNode;
  disabled?: boolean;
  className?: string;
};

type RadioSingletonProps<Option extends RadioOption> = WrapperProperties &
  GroupProps &
  RadioBaseProps & {
    className?: string;
    selectedClassName?: string;
    orientation?: Orientation;

    containerClassName?: string;

    options: Option[];

    render?: (props: RenderBaseParams & Option & { selected?: boolean }) => ReactNode;
  };

const wrapperProps = [
  'alignItems',
  'gap',
  'flexWrap',
  'fullHeight',
  'fullWidth',
  'justifyContent',
  'inLine',
  'position',
  'relative',
];

const groupProps = [
  'color',
  'size',
  'disabled',
  'name',
  'id',
  'defaultValue',
  'value',
  'onChangeDirect',
  'onChange',
];

export function RadioInput<Option extends RadioOption>({
  orientation = 'vertical',
  gap = '2',
  options,
  render,
  icon,
  iconSize,
  className,
  addDot,
  selectedClassName,
  containerClassName,
  ...props
}: RadioSingletonProps<Option>): JSX.Element {
  return (
    <RadioGroup {...(pick(props, groupProps as any) as any)}>
      <RowColumn
        {...pick(props, wrapperProps as any)}
        orientation={orientation}
        gap={gap}
        className={containerClassName}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            icon={icon}
            iconSize={iconSize}
            addDot={addDot}
            {...option}
            className={cn(
              className,
              option.className,
              !!props.value && option.value === props.value && selectedClassName,
            )}
            render={
              render
                ? ({ radioDot }) =>
                    render({
                      ...option,
                      radioDot,
                      selected: !!props.value && option.value === props.value,
                    })
                : undefined
            }
          />
        ))}
      </RowColumn>
    </RadioGroup>
  );
}
